import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart3,
  HelpCircle,
  RefreshCw,
  RotateCcw,
  Share2
} from "lucide-react";
import {
  GameMode,
  GameStatus,
  GuessRow,
  LetterState,
  MAX_ATTEMPTS,
  WORD_LENGTH,
  getDailyWord,
  getFreeWord,
  isAcceptedWord,
  mergeKeyboardState,
  normalizeWord,
  scoreGuess
} from "./game";
import { bindScrollDepthTracking, initAnalytics, trackEvent } from "./analytics";
import { keywordEntries, seoContentPlan } from "./keywords";

type Stats = {
  played: number;
  won: number;
  streak: number;
  bestStreak: number;
};

const emptyStats: Stats = {
  played: 0,
  won: 0,
  streak: 0,
  bestStreak: 0
};

const keyboardRows = ["qwertyuiop", "asdfghjkl", "zxcvbnm"];

function loadStats(): Stats {
  try {
    const raw = window.localStorage.getItem("wordle-italiano-stats");
    return raw ? { ...emptyStats, ...JSON.parse(raw) } : emptyStats;
  } catch {
    return emptyStats;
  }
}

function saveStats(stats: Stats) {
  window.localStorage.setItem("wordle-italiano-stats", JSON.stringify(stats));
}

function AdSlot({ position }: { position: "top" | "right" | "bottom" | "left" }) {
  const enteredAt = useRef<number | null>(null);

  const handleEnter = () => {
    enteredAt.current = performance.now();
  };

  const handleLeave = () => {
    if (enteredAt.current === null) {
      return;
    }
    const dwellMs = Math.round(performance.now() - enteredAt.current);
    enteredAt.current = null;
    if (dwellMs > 400) {
      trackEvent("ad_dwell", { slot: position, dwellMs });
    }
  };

  return (
    <aside
      className={`ad-zone ad-zone-${position}`}
      data-ad-slot={position}
      aria-label={`Spazio pubblicitario ${position}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={() => trackEvent("ad_click", { slot: position })}
    />
  );
}

function Tile({ letter, state }: { letter?: string; state: LetterState }) {
  return (
    <div className={`tile tile-${state}`} aria-label={letter ? `${letter} ${state}` : "vuota"}>
      {letter}
    </div>
  );
}

function Board({ guesses, current }: { guesses: GuessRow[]; current: string }) {
  const rows = Array.from({ length: MAX_ATTEMPTS }, (_, rowIndex) => {
    if (guesses[rowIndex]) {
      return guesses[rowIndex];
    }
    if (rowIndex === guesses.length) {
      return {
        word: current.padEnd(WORD_LENGTH, " "),
        result: Array<LetterState>(WORD_LENGTH).fill("empty")
      };
    }
    return {
      word: " ".repeat(WORD_LENGTH),
      result: Array<LetterState>(WORD_LENGTH).fill("empty")
    };
  });

  return (
    <div className="board" role="grid" aria-label="Griglia Wordle Italiano">
      {rows.map((row, rowIndex) => (
        <div className="board-row" role="row" key={`${row.word}-${rowIndex}`}>
          {Array.from({ length: WORD_LENGTH }, (_, colIndex) => (
            <Tile
              key={`${rowIndex}-${colIndex}`}
              letter={row.word[colIndex]?.trim().toUpperCase()}
              state={row.result[colIndex]}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function Keyboard({
  states,
  disabled,
  onLetter,
  onEnter,
  onBackspace
}: {
  states: Record<string, LetterState>;
  disabled: boolean;
  onLetter: (letter: string) => void;
  onEnter: () => void;
  onBackspace: () => void;
}) {
  return (
    <div className="keyboard" aria-label="Tastiera virtuale">
      {keyboardRows.map((row, rowIndex) => (
        <div className="keyboard-row" key={row}>
          {rowIndex === 2 ? (
            <button
              className="key key-wide"
              type="button"
              disabled={disabled}
              onClick={onEnter}
            >
              Invio
            </button>
          ) : null}
          {row.split("").map((letter) => (
            <button
              className={`key key-${states[letter] ?? "empty"}`}
              key={letter}
              type="button"
              disabled={disabled}
              onClick={() => onLetter(letter)}
            >
              {letter.toUpperCase()}
            </button>
          ))}
          {rowIndex === 2 ? (
            <button
              className="key key-wide"
              type="button"
              aria-label="Cancella"
              disabled={disabled}
              onClick={onBackspace}
            >
              <RotateCcw size={17} strokeWidth={2.4} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function ResultBar({ status, target }: { status: GameStatus; target: string }) {
  if (status === "playing") {
    return null;
  }

  return (
    <div className={`result-bar result-${status}`} role="status" aria-live="polite">
      {status === "won" ? "Hai indovinato." : `La parola era ${target.toUpperCase()}.`}
    </div>
  );
}

function SeoSection() {
  return (
    <section className="seo-band" id="strategie" aria-labelledby="strategie-title">
      <div className="seo-inner">
        <div className="seo-copy">
          <p className="eyebrow">wordle italiano</p>
          <h2 id="strategie-title">Gioca a Wordle in italiano</h2>
          <p>
            Wordle Italiano porta la formula del puzzle quotidiano dentro un lessico
            naturale per chi gioca dall'Italia. La pagina punta sulla parola chiave
            principale "wordle italiano" e copre ricerche correlate come "wordle in
            italiano", "wordle italiano senza limiti" e "parole wordle italiano".
          </p>
          <h3>Strategia per parole italiane</h3>
          <p>
            Parti con parole ricche di vocali, osserva le consonanti confermate e
            cambia schema quando una lettera resta grigia per due turni consecutivi.
          </p>
          <h4>Frequenza delle lettere</h4>
          <p>
            In italiano vocali come A, E, I e O aiutano a restringere subito il campo.
            Le consonanti R, L, T, N e S sono buone candidate per le prime due prove.
          </p>
          <h5>Parole iniziali consigliate</h5>
          <p>Prova AMORE, STILE, PORTA, LINEA o CANTO per bilanciare vocali e consonanti.</p>
          <h6>Note editoriali</h6>
          <p>
            I contenuti sono scritti per utenti italiani e mantengono una densita
            naturale, senza ripetizioni forzate.
          </p>
        </div>
        <div className="keyword-panel" aria-label="Keyword SEO">
          {keywordEntries.slice(0, 6).map((entry) => (
            <div className="keyword-row" key={entry.keyword}>
              <span>{entry.keyword}</span>
              <strong>{entry.monthlySearches.toLocaleString("it-IT")}</strong>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section className="faq-band" id="faq" aria-labelledby="faq-title">
      <div className="faq-inner">
        <p className="eyebrow">FAQ</p>
        <h2 id="faq-title">Domande frequenti su Wordle Italiano</h2>
        <details>
          <summary>Che cos'e Wordle Italiano?</summary>
          <p>
            E un gioco online in cui devi indovinare una parola italiana di cinque
            lettere in sei tentativi.
          </p>
        </details>
        <details>
          <summary>Esiste una modalita senza limiti?</summary>
          <p>
            Si. Usa il pulsante di nuova partita per giocare un altro round quando
            vuoi.
          </p>
        </details>
        <details>
          <summary>Quali parole conviene usare all'inizio?</summary>
          <p>
            Parole come AMORE, STILE, PORTA e CANTO danno una buona copertura di
            vocali e consonanti comuni.
          </p>
        </details>
        <nav className="internal-links" aria-label="Link interni SEO">
          {seoContentPlan.internalLinks.map((link) => (
            <a href={link.href} key={link.anchor}>
              {link.anchor}
            </a>
          ))}
        </nav>
      </div>
    </section>
  );
}

export default function App() {
  const [mode, setMode] = useState<GameMode>("daily");
  const [target, setTarget] = useState(() => getDailyWord());
  const [guesses, setGuesses] = useState<GuessRow[]>([]);
  const [current, setCurrent] = useState("");
  const [status, setStatus] = useState<GameStatus>("playing");
  const [message, setMessage] = useState("Indovina la parola italiana in sei tentativi.");
  const [keyboardState, setKeyboardState] = useState<Record<string, LetterState>>({});
  const [stats, setStats] = useState<Stats>(() => loadStats());

  useEffect(() => {
    initAnalytics();
    trackEvent("game_start", { mode, targetLength: WORD_LENGTH });
    return bindScrollDepthTracking();
  }, []);

  const resetGame = useCallback(
    (nextMode: GameMode = mode) => {
      const seed = Date.now() + guesses.length;
      setMode(nextMode);
      setTarget(nextMode === "daily" ? getDailyWord() : getFreeWord(seed));
      setGuesses([]);
      setCurrent("");
      setStatus("playing");
      setKeyboardState({});
      setMessage(
        nextMode === "daily"
          ? "Parola del giorno pronta."
          : "Nuova parola libera pronta."
      );
      trackEvent("new_game", { mode: nextMode });
    },
    [guesses.length, mode]
  );

  const finishGame = useCallback((nextStatus: GameStatus, attemptCount: number) => {
    setStatus(nextStatus);
    setStats((previous) => {
      const won = nextStatus === "won";
      const nextStats = {
        played: previous.played + 1,
        won: previous.won + (won ? 1 : 0),
        streak: won ? previous.streak + 1 : 0,
        bestStreak: won ? Math.max(previous.bestStreak, previous.streak + 1) : previous.bestStreak
      };
      saveStats(nextStats);
      return nextStats;
    });
    trackEvent(nextStatus === "won" ? "game_win" : "game_loss", {
      attempts: attemptCount,
      mode
    });
  }, [mode]);

  const addLetter = useCallback(
    (letter: string) => {
      if (status !== "playing") {
        return;
      }
      setCurrent((value) =>
        value.length < WORD_LENGTH ? normalizeWord(`${value}${letter}`) : value
      );
    },
    [status]
  );

  const backspace = useCallback(() => {
    if (status !== "playing") {
      return;
    }
    setCurrent((value) => value.slice(0, -1));
  }, [status]);

  const submitGuess = useCallback(() => {
    if (status !== "playing") {
      return;
    }

    const guess = normalizeWord(current);
    if (guess.length !== WORD_LENGTH) {
      setMessage("Servono cinque lettere.");
      return;
    }

    if (!isAcceptedWord(guess)) {
      setMessage("Parola non presente nel dizionario del gioco.");
      return;
    }

    const result = scoreGuess(guess, target);
    const nextGuesses = [...guesses, { word: guess, result }];
    setGuesses(nextGuesses);
    setKeyboardState((previous) => mergeKeyboardState(previous, guess, result));
    setCurrent("");
    trackEvent("guess_submit", { mode, attempt: nextGuesses.length });

    if (guess === target) {
      setMessage("Perfetto.");
      finishGame("won", nextGuesses.length);
      return;
    }

    if (nextGuesses.length >= MAX_ATTEMPTS) {
      setMessage(`Fine partita. La parola era ${target.toUpperCase()}.`);
      finishGame("lost", nextGuesses.length);
      return;
    }

    setMessage("Continua.");
  }, [current, finishGame, guesses, mode, status, target]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (event.key === "Enter") {
        submitGuess();
      } else if (event.key === "Backspace") {
        backspace();
      } else if (/^[a-zA-Z]$/.test(event.key)) {
        addLetter(event.key);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [addLetter, backspace, submitGuess]);

  const shareText = useMemo(() => {
    const resultRows = guesses
      .map((guess) =>
        guess.result
          .map((state) => {
            if (state === "correct") return "🟩";
            if (state === "present") return "🟨";
            return "⬜";
          })
          .join("")
      )
      .join("\n");
    const attemptLabel = status === "won" ? `${guesses.length}/${MAX_ATTEMPTS}` : `X/${MAX_ATTEMPTS}`;
    return `Wordle Italiano ${attemptLabel}\n${resultRows}\nhttps://wordleitaliano.vip/`;
  }, [guesses, status]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "Wordle Italiano", text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
        setMessage("Risultato copiato.");
      }
      trackEvent("share_result", { mode, status });
    } catch {
      setMessage("Condivisione non completata.");
    }
  };

  const winRate = stats.played ? Math.round((stats.won / stats.played) * 100) : 0;

  return (
    <div className="page-shell">
      <AdSlot position="top" />
      <AdSlot position="left" />
      <main className="main-area">
        <section className="game-stage" aria-labelledby="main-title">
          <header className="topbar">
            <div>
              <p className="eyebrow">wordleitaliano.vip</p>
              <h1 id="main-title">Wordle Italiano</h1>
            </div>
            <div className="icon-actions">
              <button
                type="button"
                className="icon-button"
                title="Statistiche"
                aria-label="Statistiche"
                onClick={() =>
                  setMessage(
                    `${stats.played} partite, ${winRate}% vinte, serie ${stats.streak}.`
                  )
                }
              >
                <BarChart3 size={20} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="icon-button"
                title="Suggerimento"
                aria-label="Suggerimento"
                onClick={() => setMessage("Usa prima vocali diverse, poi stringi sulle consonanti.")}
              >
                <HelpCircle size={20} aria-hidden="true" />
              </button>
            </div>
          </header>

          <div className="mode-switch" id="modalita-libera" role="tablist" aria-label="Modalita">
            <button
              type="button"
              className={mode === "daily" ? "active" : ""}
              aria-selected={mode === "daily"}
              onClick={() => resetGame("daily")}
            >
              Oggi
            </button>
            <button
              type="button"
              className={mode === "free" ? "active" : ""}
              aria-selected={mode === "free"}
              onClick={() => resetGame("free")}
            >
              Libera
            </button>
          </div>

          <div className="status-line" role="status" aria-live="polite">
            {message}
          </div>

          <Board guesses={guesses} current={current} />
          <ResultBar status={status} target={target} />

          <Keyboard
            states={keyboardState}
            disabled={status !== "playing"}
            onLetter={addLetter}
            onEnter={submitGuess}
            onBackspace={backspace}
          />

          <footer className="game-actions">
            <button type="button" className="command-button" onClick={() => resetGame("free")}>
              <RefreshCw size={18} aria-hidden="true" />
              Nuova
            </button>
            <button
              type="button"
              className="command-button"
              onClick={handleShare}
              disabled={guesses.length === 0}
            >
              <Share2 size={18} aria-hidden="true" />
              Condividi
            </button>
          </footer>
        </section>

        <section className="quick-stats" aria-label="Statistiche">
          <div>
            <span>{stats.played}</span>
            <small>partite</small>
          </div>
          <div>
            <span>{winRate}%</span>
            <small>vittorie</small>
          </div>
          <div>
            <span>{stats.bestStreak}</span>
            <small>serie max</small>
          </div>
        </section>
      </main>
      <AdSlot position="right" />
      <AdSlot position="bottom" />
      <SeoSection />
      <FaqSection />
    </div>
  );
}
